import _ from 'lodash'

export const MAX_BODY_REQUEST = 20000
export const MAX_BODY_RESPONSE = 10000

export const REDACTED_ENDPOINTS = [
  'data:application/wasm',
  'uploadcare.com',
  'ucarecdn.com',
  'uploadcare.withvector',
  '/actions/system/sensitive-file/',
  'entities/actions/system/oauth2/token',
]

export const shouldRedact = (url?: string): boolean => {
  if (!url) return false
  const normalizedUrl = url.toLowerCase()
  return REDACTED_ENDPOINTS.some((pattern) => normalizedUrl.includes(pattern))
}

export function stripAuthHeader(headers?: Record<string, string | null | undefined>): void {
  if (!headers) {
    return
  }
  for (const key in headers) {
    if (key.toLowerCase() === 'authorization' && !_.isNil(headers[key])) {
      headers[key] = 'Bearer ********'
    }
  }
}

export function truncateRequestBody(rawBody: string | undefined, method?: string): string {
  const parsed = JSON.parse(rawBody ?? '{}')
  return JSON.stringify({
    uniqueId: _.get(parsed, 'uniqueId'),
    method,
  })
}

export function truncateResponseBody(rawBody: string | undefined): string {
  const parsed = JSON.parse(rawBody ?? '{}')
  const children = Array.isArray(parsed) ? parsed : _.get(parsed, 'children', [])
  const key = Array.isArray(parsed) ? 'uniqueId' : 'data.uniqueId'
  const newBody = []
  for (const child of children) {
    newBody.push({
      uniqueId: _.get(child, key),
    })
  }
  return JSON.stringify(newBody)
}

export function isReadRequest(request: { method?: string; url?: string }): boolean {
  const method = request.method?.toUpperCase()
  const url = request.url?.toLowerCase() || ''

  switch (method) {
    case 'GET':
      return true
    case 'POST':
      return url.includes('/_mget') || url.includes('/query')
    case 'PUT':
    case 'PATCH':
    case 'DELETE':
      return false
    default:
      return true
  }
}

/**
 * Creates a rate limiter function for controlling request frequency.
 *
 * The returned function can be called before sending a network request to determine
 * whether it should be allowed or dropped based on the configured rate limit.
 *
 * @param {Object} options
 * @param {number} options.limit - Maximum number of allowed requests within the time window.
 * @param {number} options.windowMs - Duration of the time window in milliseconds.
 *
 * @returns {function(): boolean} A function that returns `true` if the request is allowed,
 *   or `false` if the rate limit has been exceeded. Also logs warnings when throttling
 *   starts or stops.
 */
export const createRateLimiter = ({
  limit,
  windowMs,
}: {
  limit: number
  windowMs: number
}): (() => boolean) => {
  let requestTimestamps: number[] = []
  let isThrottling = false

  return () => {
    const now = Date.now()

    // Remove timestamps that are outside the current window
    requestTimestamps = requestTimestamps.filter((timestamp) => now - timestamp < windowMs)

    const shouldAllow = requestTimestamps.length < limit

    // not throttling -> throttle
    if (!shouldAllow && !isThrottling) {
      isThrottling = true
      // console log is also captured by LogRocket, so no need to send a separate log to LogRocket
      console.warn(
        `[LogRocket] Network rate limit of ${limit}/${windowMs / 1000}s exceeded. ` +
          `Starting to drop network request and response bodies.`
      )
    }

    // throttled -> no throttling
    if (shouldAllow && isThrottling) {
      isThrottling = false
      console.warn(
        `[LogRocket] Network traffic is back below the limit. Resuming network recording.`
      )
    }

    if (shouldAllow) {
      // limit not reached, add the new timestamp and allow the request
      requestTimestamps.push(now)
      return true
    } else {
      return false
    }
  }
}

// per AI overlord's recommendation
const MAX_REQUESTS_PER_WINDOW = 10 // allow X requests...
const RATE_LIMIT_WINDOW_MS = 5 * 1000 // ...within a Y-second window

export const networkRateLimiter = createRateLimiter({
  limit: MAX_REQUESTS_PER_WINDOW,
  windowMs: RATE_LIMIT_WINDOW_MS,
})
